import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Scissors, Palette, Hand, Footprints, Sparkles, Flower2, ArrowRight, Star, ShieldCheck, HeartHandshake, Award } from "lucide-react";
import AIConciergeWidget from "@/components/AIConciergeWidget";
import DemoTopNav from "@/components/demos/DemoTopNav";
import DemoBookingModal from "@/components/demos/DemoBookingModal";
import { DEMO_THEMES } from "@/data/demoThemes";

const GOLD = "#B58A5A";
const DARK = "#3A2E22";
const CREAM = "#FBF6F0";
const serif = { fontFamily: "'Playfair Display', serif" };
const body = { fontFamily: "'Plus Jakarta Sans', sans-serif" };
const IMG = "https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f?crop=entropy&cs=srgb&fm=jpg&w=1100&q=80&ixlib=rb-4.1.0";
const SERVICES = [
  { icon: Scissors, t: "Hair Styling", p: "from $55" },
  { icon: Palette, t: "Hair Color", p: "from $95" },
  { icon: Hand, t: "Manicure", p: "from $35" },
  { icon: Footprints, t: "Pedicure", p: "from $50" },
  { icon: Sparkles, t: "Nail Art", p: "from $45" },
  { icon: Flower2, t: "Spa Treatments", p: "from $75" },
];
const TRUST = [{ icon: Award, t: "Expert Stylists" }, { icon: ShieldCheck, t: "Clean & Safe" }, { icon: HeartHandshake, t: "All Are Welcome" }];

export default function LumeDemo() {
  const [book, setBook] = useState(false);
  const [preService, setPreService] = useState(null);
  useEffect(() => { window.scrollTo(0, 0); document.title = "Lumé Hair & Nail Spa (Demo) | WebsiteTech.pro"; }, []);
  const selects = [
    { key: "service", label: "Service", options: ["Hair Styling", "Hair Color", "Manicure", "Pedicure", "Nail Art", "Spa Treatments"] },
    { key: "stylist", label: "Stylist Preference", options: ["Any Stylist", "Nadia", "Priya", "Jordan", "Mei"] },
  ];

  return (
    <div className="min-h-screen" style={{ background: CREAM, color: DARK, ...body }} data-testid="salon-demo">
      <Link to="/demos" className="block w-full text-center text-[11px] tracking-widest uppercase py-1.5 text-white" style={{ background: GOLD }}>WebsiteTech.pro Demo · Salon & Beauty — view all demos</Link>
      <div className="text-center text-xs py-2" style={{ background: "#F0E4D3", color: DARK }}><span className="font-semibold uppercase tracking-wide" style={{ color: GOLD }}>Today's Special:</span> 20% off for first-time guests</div>
      <DemoTopNav testid="salon-nav"
        logo={<div className="leading-tight"><div className="text-xl tracking-[0.22em]" style={{ ...serif, color: DARK }}>LUMÉ</div><div className="text-[8px] tracking-[0.35em] uppercase" style={{ color: GOLD }}>Hair & Nail Spa</div></div>}
        items={["Home", "Services", "Gallery", "Specials", "About", "Contact"]} ctaLabel="Book an Appointment" onCta={() => setBook(true)}
        theme={{ bg: "rgba(251,246,240,0.94)", text: DARK, accent: GOLD, accentText: "#fff", border: "#ecdfce" }} />

      <header data-testid="salon-hero" className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-8 items-center py-14">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <span className="text-sm italic" style={{ ...serif, color: GOLD }}>Look good · Feel good · Belong here</span>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" style={serif}>Beauty,<br />styled for you.</h1>
          <p className="mt-5 text-lg opacity-75 max-w-md">Hair, nails, and self-care — all in one beautiful place.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={() => setBook(true)} data-testid="salon-book-hero" className="rounded-full px-7 py-3.5 text-xs tracking-[0.18em] uppercase font-semibold text-white flex items-center gap-2" style={{ background: GOLD }}>Book an Appointment <ArrowRight size={14} /></button>
            <button className="rounded-full px-7 py-3.5 text-xs tracking-[0.18em] uppercase font-semibold" style={{ border: `1px solid ${GOLD}66`, color: DARK }}>View Services</button>
          </div>
        </motion.div>
        <motion.img initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} src={IMG} alt="Salon" className="rounded-2xl h-[380px] w-full object-cover shadow-lg" />
      </header>

      <section data-testid="salon-services" className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-center mb-10"><span className="text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: GOLD }}>Our Services</span><h2 className="mt-2 text-3xl" style={serif}>Everything for a brighter you</h2></div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {SERVICES.map((s, i) => (
            <button key={s.t} onClick={() => { setPreService(s.t); setBook(true); }} className="group text-left bg-white rounded-xl p-6 border hover:shadow-md transition-shadow flex items-center gap-4" style={{ borderColor: "#ecdfce" }} data-testid={`salon-service-${i}`}>
              <div className="h-11 w-11 rounded-full grid place-items-center shrink-0" style={{ background: `${GOLD}1a`, color: GOLD }}><s.icon size={20} /></div>
              <div className="flex-1"><div className="font-semibold">{s.t}</div><div className="text-xs" style={{ color: GOLD }}>{s.p}</div></div>
              <ArrowRight size={15} className="opacity-30 group-hover:opacity-100 transition-opacity" />
            </button>
          ))}
        </div>
      </section>

      <section className="py-16" style={{ background: "#F3E9DB" }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <span className="text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: GOLD }}>More Than a Salon</span>
          <h2 className="mt-3 text-3xl" style={serif}>A brighter you</h2>
          <div className="mt-8 grid grid-cols-3 gap-6">
            {TRUST.map((t) => (<div key={t.t} className="flex flex-col items-center gap-2"><t.icon size={24} style={{ color: GOLD }} /><span className="text-sm font-medium">{t.t}</span></div>))}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-16 text-center">
        <div className="flex justify-center gap-1 mb-3" style={{ color: GOLD }}>{[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}</div>
        <p className="text-2xl md:text-3xl leading-relaxed" style={serif}>"I always leave Lumé feeling like the best version of myself. The team is talented, warm, and welcoming."</p>
        <div className="mt-4 text-sm opacity-60">— Renée T.</div>
      </section>

      <footer data-testid="salon-footer" className="text-white" style={{ background: DARK }}>
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4 text-sm">
          <div style={serif} className="tracking-[0.22em] text-lg">LUMÉ</div>
          <p className="opacity-70">Tue–Sun · 9:00 AM – 7:00 PM</p>
          <p className="opacity-70 italic" style={serif}>Look good. Feel good. Belong here.</p>
        </div>
      </footer>

      {book && <DemoBookingModal demo="salon" title="Book an appointment" accent={GOLD} submitLabel="Request appointment" selects={selects} prefill={preService ? { service: preService } : {}} onClose={() => { setBook(false); setPreService(null); }} />}
      <AIConciergeWidget demo="salon" concierge={DEMO_THEMES.salon.concierge} />
    </div>
  );
}
